import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import imageCompression from 'browser-image-compression';
import Alert from '../Alert/Alert';

const InputImages = ({
  images,
  previews,
  setImages,
  setPreviews,
  goToStep,
  editMode,
  editActive,
  setNewImages,
  setImagesToDelete
}) => {
  const [showError, setShowError] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Auto-cerrar el alert de error de imagen después de 8 segundos
  useEffect(() => {
    if (imageError) {
      const timer = setTimeout(() => {
        setImageError(null);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [imageError]);

  // Detectar si es iOS (detección ultra-precisa de Gemini)
  const isIOS = () => {
    const ua = navigator.userAgent;
    const isMacWithTouch = navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
    return /iPad|iPhone|iPod/.test(ua) || isMacWithTouch;
  };

  // Comprimir imagen (estrategia de Gemini)
  const compressImage = async (file) => {
    try {
      // Validar formato primero
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setImageError('Solo se permiten archivos PNG, JPEG y WebP.');
        return null;
      }

      const isiOS = isIOS();

      // VALIDACIÓN DEL TAMAÑO ORIGINAL (lógica anterior)
      // iOS: 400KB máximo (compensa EXIF extra)
      // Otros: 300KB máximo
      const maxSizeInBytes = isiOS ? 400 * 1024 : 300 * 1024;
      const maxSizeKB = isiOS ? 400 : 300;

      if (file.size > maxSizeInBytes) {
        const realKB = (file.size / 1024).toFixed(1);
        setImageError(`La imagen "${file.name}" pesa ${realKB} KB. Debe pesar máximo ${maxSizeKB} KB.`);
        return null;
      }

      // COMPRESIÓN (estrategia de Gemini)
      const options = {
        useWebWorker: true,
        preserveExif: false,
        // iOS: 1280px para evitar subsampling, Otros: 1920px
        maxWidthOrHeight: isiOS ? 1280 : 1920,
        // iOS: JPEG (hardware optimizado), Otros: WebP
        fileType: isiOS ? 'image/jpeg' : 'image/webp',
        // Ajuste de peso objetivo
        maxSizeMB: isiOS ? 0.35 : 0.28,
        // Calidad inicial
        initialQuality: isiOS ? 0.75 : 0.85,
      };

      const compressedBlob = await imageCompression(file, options);

      // Generar extensión correcta según el tipo procesado
      const extension = isiOS ? 'jpg' : 'webp';
      const mimeType = isiOS ? 'image/jpeg' : 'image/webp';

      const compressedFile = new File(
        [compressedBlob],
        file.name.replace(/\.[^/.]+$/, `.${extension}`),
        { type: mimeType, lastModified: Date.now() }
      );

      return compressedFile;
    } catch (error) {
      setImageError(`Error al procesar "${file.name}". Intenta con otra imagen.`);
      return null;
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);

    if (files.length + images.length > 6) {
      setShowError(true);
      event.target.value = '';
      return;
    }

    setIsProcessing(true);

    // Procesar todas las imágenes una por una
    const processedImages = [];
    
    for (const file of files) {
      const compressed = await compressImage(file);
      if (compressed) {
        processedImages.push(compressed);
      }
    }

    // Agregar solo las imágenes procesadas exitosamente
    if (processedImages.length > 0) {
      const newPreviews = processedImages.map((img) =>
        URL.createObjectURL(img)
      );
      setPreviews((prev) => [...prev, ...newPreviews]);
      setImages((prev) => [...prev, ...processedImages]);

      if (editMode && editActive && typeof setNewImages === 'function') {
        setNewImages((prev) => [...prev, ...processedImages]);
      }
    }

    setIsProcessing(false);
    event.target.value = '';
  };

  const handleRemoveImage = (index) => {
    const imageToRemove = images[index];
    setPreviews(previews.filter((_, i) => i !== index));
    setImages(images.filter((_, i) => i !== index));

    if (editMode && editActive) {
      // ✅ Si es una imagen que ya existe (tiene id), márcala para borrar
      if (imageToRemove?.id && typeof setImagesToDelete === 'function') {
        setImagesToDelete((prev) => [...prev, imageToRemove]);
      }

      // ✅ Si era una imagen nueva, elimínala también de newImages
      if (!imageToRemove?.id && typeof setNewImages === 'function') {
        setNewImages((prev) =>
          prev.filter((img) => img !== imageToRemove)
        );
      }
    }
  };

  const isEditable = !editMode || editActive;

  return (
    <>
      {imageError && (
        <Alert
          show={true}
          type="error"
          message={imageError}
          onClose={() => setImageError(null)}
        />
      )}

      {showError && (
        <Alert
          show={true}
          type="error"
          message="Puedes subir hasta 6 imágenes máximo."
          onClose={() => setShowError(false)}
        />
      )}

      {isProcessing && (
        <Alert
          show={true}
          type="info"
          message="Comprimiendo imágenes..."
          onClose={() => {}}
        />
      )}

      <div className="step slide-in">
        {!editMode && (
          <div className="step-sup">
            <div>
              <h1>Paso 1 de 3</h1>
              <h2>Sube imágenes del producto</h2>
            </div>
          </div>
        )}
        <div className="image-preview-container">
          {previews.map((src, index) => (
            <div key={index} className="preview-box">
              <img src={src} alt={`preview-${index}`} />
              {isEditable && (
                <FontAwesomeIcon
                  icon={faCircleXmark}
                  className="delete-icon"
                  onClick={() => handleRemoveImage(index)}
                />
              )}
            </div>
          ))}

          {isEditable && images.length < 6 && (
            <>
              <div
                className={`preview-box ${isProcessing ? 'processing' : ''}`}
                onClick={() => !isProcessing && document.getElementById('hidden-file-input').click()}
                style={{ cursor: isProcessing ? 'not-allowed' : 'pointer', opacity: isProcessing ? 0.5 : 1 }}
              >
                <span className="add-image">
                  {isProcessing ? (
                    <div className="spinner">⏳</div>
                  ) : (
                    <FontAwesomeIcon icon={faPlus} />
                  )}
                </span>
                <input
                  id="hidden-file-input"
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  multiple
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                  disabled={isProcessing}
                />
              </div>
            </>
          )}
        </div>

        {!editMode && (
          <button
            type="button"
            onClick={() => goToStep(1)}
          >
            Siguiente
          </button>
        )}
      </div>
    </>
  );
};

export default InputImages;
